import styled from '@emotion/styled'
import { useResizeDetector } from 'react-resize-detector'
import start from './unsafe-startup'
import CBOR from 'cbor-js'
import * as React from 'react'
import * as R from 'ramda'
import ReactDOM from 'react-dom'
import {
  Center,
  ChakraProvider,
  extendTheme,
  Flex,
  Box,
  VStack,
  Heading,
} from '@chakra-ui/react'

import type { ThemeConfig } from '@chakra-ui/react'

import type { GameState, PlayerState } from './types'
import type {
  ClientAuthMessage,
  ServerMessage,
  SocketMessage,
  CommandMessage,
  PacketMessage,
} from './protocol'
import { GameStateType, WeaponType } from './types'
import { MessageType, ENetEventType } from './protocol'
import StatusOverlay from './Loading'
import NAMES from './names'
import useAssets, { getInstalledMods, mountFile } from './assets/hook'
import useAuth, {
  DISCORD_CODE,
  renderDiscordHeader,
  renderDiscordButton,
} from './discord'
import { CubeMessageType } from './game'
import * as cube from './game'
import MobileControls from './MobileControls'
import FileDropper from './FileDropper'

import type { PromiseSet } from './utils'
import { CONFIG } from './config'
import { breakPromise, BROWSER } from './utils'
import * as log from './logging'

import { LoadRequestType } from './assets/types'

start()

const colors = {
  brand: {
    900: '#1a365d',
    800: '#153e75',
    700: '#2a69ac',
  },
}

const config: ThemeConfig = {
  initialColorMode: 'dark',
  useSystemColorMode: false,
}

const theme = extendTheme({ colors, config })

const OuterContainer = styled.div`
  touch-action: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`

const GameContainer = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  z-index: 0;
`

const LoadingContainer = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  z-index: 1;
`

const DropTarget = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  pointer-events: none;
  background: var(--chakra-colors-yellow-500);
  z-index: 2;
`

const encodeCommandValue = (value: string): string => {
  const bytes = new TextEncoder().encode(value)
  let binary = ''
  bytes.forEach((byte) => { binary += String.fromCharCode(byte) })
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

const pushURLState = (url: string) => {
  const {
    location: { search: params },
  } = window

  // We only want to keep 'mods', everything else goes
  const parsedParams = new URLSearchParams(params)
  const newParams = new URLSearchParams()
  if (parsedParams.has('mods')) {
    const value = parsedParams.get('mods')
    if (value != null) {
      newParams.set('mods', value)
    }
  }

  window.history.pushState({}, '', `${url}${newParams.toString()}`)
}

const clearURLState = () => pushURLState('/')

const MODE_NAMES = [
  'ffa',
  'coop-edit',
  'teamplay',
  'insta',
  'insta team',
  'efficiency',
  'efficiency team',
  'tactics',
  'tactics team',
  'capture',
  'regen capture',
  'ctf',
  'insta ctf',
  'protect',
  'insta protect',
  'hold',
  'insta hold',
  'efficiency ctf',
  'efficiency protect',
  'efficiency hold',
  'collect',
  'insta collect',
  'efficiency collect',
] as const
const modeName = (id: number): string => MODE_NAMES[id] ?? `mode ${id}`

export type CommandRequest = {
  id: number
  promiseSet: PromiseSet<string>
}

const DEBUG = false
const DELAY_AFTER_LOAD: CubeMessageType[] = [
  CubeMessageType.N_ITEMLIST,
  CubeMessageType.N_SPAWN,
]

const SERVER_URL_REGEX = /#\/server\/([\w.]+)\/?(\d+)?/
const MAP_URL_REGEX = /#\/map\/(\w+)/
const DEMO_URL_REGEX = /#\/demo\/(\w+)/

let loadedMods: string[] = []
let failedMods: string[] = []

async function playDemoURL(url: string, reference: string) {
  log.info(`loading demo ${reference}`)
  try {
    const response = await fetch(url)
    if (response.status === 404) {
      log.error(`demo ${reference} not found`)
      return
    }
    if (response.status !== 200) {
      throw Error('failed to fetch')
    }

    const data = await response.arrayBuffer()
    const demoName = `/demo/${reference}.dmo`
    await mountFile(demoName, new Uint8Array(data))
    BananaBread.execute(`demo ${reference}.dmo`)
  } catch (e) {
    log.error('failed to play demo')
  }
}

function App() {
  const [state, setState] = React.useState<GameState>({
    type: GameStateType.PageLoading,
  })
  const { width, height, ref: containerRef } = useResizeDetector()

  const canvasRef = React.useRef<HTMLCanvasElement>(null)
  const wsRef = React.useRef<WebSocket>()
  const wsQueue = React.useRef<ArrayBuffer[]>([])

  const send = React.useCallback((data: ArrayBuffer) => {
    const { current: ws } = wsRef
    const { current: queue } = wsQueue
    if (ws == null || ws.readyState !== WebSocket.OPEN) {
      wsQueue.current = [...queue, data]
      return
    }
    ws.send(data)
  }, [])

  const sendAuthMessage = React.useCallback((message: ClientAuthMessage) => {
    send(CBOR.encode(message))
  }, [])

  const {
    loadAsset,
    loadAssetProgress,
    getMod,
    onReady: onReadyAssets,
  } = useAssets(setState)
  const {
    state: authState,
    receiveMessage: receiveAuthMessage,
    initialize: initializeDiscord,
  } = useAuth(sendAuthMessage)

  const [playerState, setPlayerState] = React.useState<PlayerState>({
    health: 0,
    maxHealth: 0,
    weapon: WeaponType.Pistol,
    ammo: {
      [WeaponType.Saw]: 0,
      [WeaponType.Shotgun]: 0,
      [WeaponType.Chaingun]: 0,
      [WeaponType.Rocket]: 0,
      [WeaponType.Rifle]: 0,
      [WeaponType.Grenade]: 0,
      [WeaponType.Pistol]: 0,
    },
  })


  const internalServersRef = React.useRef<string>(`newgui integrated [
        guitab "servers"
        guiservers [
            guilist [
                guicheckbox \"auto-sort\" autosortservers
                if (= $autosortservers 0) [ guibar ; guibutton \"sort\" \"sortservers\" ]
            ]
            guibar
        ] 17
    ] "" [initservers]
  `)
  const [internalServers, _setInternalServers] = React.useState<string>(internalServersRef.current)
  const setInternalServers = React.useCallback((gui: string) => {
    if (internalServersRef.current === gui) return
    internalServersRef.current = gui
    _setInternalServers(gui)
  }, [])

  React.useEffect(() => {
    Module.gameState = {
      playerState: (
        health: number,
        maxHealth: number,
        gunSelect: number,
        shotgunAmmo: number,
        chainggunAmmo: number,
        rocketAmmo: number,
        rifleAmmo: number,
        grenadeAmmo: number,
        pistolAmmo: number
      ) => {
        setPlayerState({
          health,
          maxHealth,
          weapon: gunSelect,
          ammo: {
            [WeaponType.Saw]: 1,
            [WeaponType.Shotgun]: shotgunAmmo,
            [WeaponType.Chaingun]: chainggunAmmo,
            [WeaponType.Rocket]: rocketAmmo,
            [WeaponType.Rifle]: rifleAmmo,
            [WeaponType.Grenade]: grenadeAmmo,
            [WeaponType.Pistol]: pistolAmmo,
          },
        })
      },
    }
  }, [])

  React.useEffect(() => {
    ;(async () => {
      // Waits for the WASM file to be downloaded and memory to be initialized
      // This solves a race condition wherein we were mounting things to the
      // filesystem before the HEAP was defined
      await WASM_PROMISE

      // Not a mod but I'm lazy
      await loadAsset(LoadRequestType.Mod, 'environment')

      // Load the basic required data for the game
      await loadAsset(LoadRequestType.Mod, 'base')
      await loadAsset(LoadRequestType.Mod, 'fps')

      const {
        location: { search: params },
      } = window

      const parsedParams = new URLSearchParams(params)
      let mods: string[] = getInstalledMods()
      if (parsedParams.has('safemode')) {
        mods = []
      }
      if (parsedParams.has('mods')) {
        const urlMods = parsedParams.get('mods')
        if (urlMods != null) {
          mods = urlMods.split(',')
        }
      }

      if (mods.length > 0) {
        await Promise.all(
          R.map(async (id: string) => {
            const mod = getMod(id)
            if (mod == null) {
              failedMods = [...failedMods, id]
              return
            }

            const { name } = mod

            try {
              const layer = await loadAsset(LoadRequestType.Mod, id)
              if (layer == null) {
                failedMods = [...failedMods, name]
                return
              }
              loadedMods = [...loadedMods, name]
            } catch (e) {
              failedMods = [...failedMods, name]
            }
          }, mods)
        )
      }

      shouldRunNow = true
      calledRun = false
      Module.calledRun = false
      Module.run()
    })()

    Module.socket = (addr, port) => {
      const { protocol, host } = window.location
      const wsProtocol = protocol === 'https:' ? 'wss:' : 'ws:'
      const basePath = new URL('service/proxy/', window.location.href).pathname
      const prefix = `${wsProtocol}//${host}${basePath}`

      return new WebSocket(
        addr === 'sour' ? prefix : `${prefix}u/${addr}:${port}`,
        ['binary']
      )
    }

    Module.print = (text) => {
      if (text === 'init: sdl') {
        setState({
          type: GameStateType.Running,
        })
      }

      // Randomly assign a new name if the user joins without one
      if (text === 'setting name to: unnamed') {
        const name = NAMES[Math.floor(Math.random() * NAMES.length)]
        BananaBread.execute(`name ${name}`)
      }

      if (text.startsWith('main loop blocker')) {
        return
      }

      console.log(text)
    }
  }, [])

  const setResolution = React.useCallback(
    (width: Maybe<number>, height: Maybe<number>) => {
      if (BROWSER.isMobile) {
        const ratio = window.devicePixelRatio || 1
        const {
          documentElement: { clientWidth, clientHeight },
        } = document
        const width = clientWidth * ratio
        const height = clientHeight * ratio
        const { canvas } = Module
        if (canvas == null) return

        if (Module.running) {
          if (BananaBread == null || BananaBread.execute == null) return
          BananaBread.execute(`screenres ${width * 2} ${height * 2}`)
        }

        canvas.style.setProperty('width', clientWidth + 'px', 'important')
        canvas.style.setProperty('height', clientHeight + 'px', 'important')
        canvas.width = width * 2
        canvas.height = height * 2
        return
      }

      if (width == null || height == null) return
      Module.desiredWidth = width
      Module.desiredHeight = height
      if (Module.setCanvasSize == null) return
      Module.setCanvasSize(width, height)
      if (BananaBread == null || BananaBread.execute == null) return
      BananaBread.execute(`screenres ${width} ${height}`)
    },
    []
  )

  React.useEffect(() => {
    setResolution(width, height)
  }, [width, height])

  React.useEffect(() => {
    if (state.type !== GameStateType.Ready) return

    // To show the server browser, add:
    //   guibutton "server browser.." "showgui servers"
    // 
    // Proxy setup is incomplete though at the moment, so
    // connecting won't work

    // Removed discord button for now
    // 
    //   newgui discord [
    //       guibutton "copy authkey command.." [js "Module.discord.copyKey()"]
    //       //guibutton "regenerate auth key.." [js "Module.discord.regenKey()"]
    //       guibutton "log out.." [js "Module.discord.logout()"]
    //   ]

    // Removed menu options for now
    // 
    //   ${CONFIG.menuOptions}
    //   guibutton "join insta-dust2" "join insta-dust2"
    //   guibutton "join ffa-dust2" "join ffa-dust2"
    //   guibutton "join insta rotating maps" "join insta"
    //   guibutton "join ffa rotating maps" "join lobby"
    //   ${renderDiscordHeader(authState)}
    //   ${renderDiscordButton(authState)}

    const menu = `
    // Native Sauerbraten custom-game menus. These deliberately live inside the
    // engine GUI so cursor locking, keyboard focus, ESC, and styling all behave
    // exactly like the rest of the game.
    cg_mode = "ffa"
    cg_mode_label = "Free For All"
    cg_map = "complex"
    cg_visibility = 2
    cg_password = ""
    cg_bots = 0
    cg_skill = 70
    cg_duration = 300
    cg_join_target = ""
    cg_join_password = ""

    cgselectmap = [
        cg_map = $arg1
        showgui customgame
    ]

    gencgmapitems = [
        looplist curmap $arg1 [
            guibutton $curmap (concatword "cgselectmap " $curmap) "cube"
        ]
    ]

    buildcgcustommaps = [
        cgcustommaps = ""
        loopfiles curmap "packages/base" "ogz" [
            if (< (indexof $allmaps $curmap) 0) [
                cgcustommaps = (concat $cgcustommaps (escape $curmap))
            ]
        ]
        cgcustommaps = (sortlist $cgcustommaps x y [naturalsort $x $y])
    ]

    cgcreate = [
        if (&& (= $cg_visibility 1) (< (strlen $cg_password) 3)) [
            echo "^f3Password must be at least 3 characters."
        ] [
            cleargui 1
            creategame (concatword $cg_mode "|" $cg_map "|" $cg_visibility "|" $cg_password "|" $cg_bots "|" $cg_skill "|" $cg_duration) ""
        ]
    ]

    newgui customgame [
        guitext "Create Custom Game" 0
        guibar
        guilist [
            guilist [
                guitext "Mode" 0
                guibutton (concatword "  " $cg_mode_label) [showgui customgamemode] "cube"
                guitext "Map" 0
                guibutton (concatword "  " $cg_map) [buildcgcustommaps; showgui customgamemaps] "cube"
                guitext "Visibility" 0
                guiradio "Public - listed for everyone" cg_visibility 0
                guiradio "Password protected - listed with a lock" cg_visibility 1
                guiradio "Unlisted - join by URL or room code" cg_visibility 2
                if (= $cg_visibility 1) [
                    guitext "Password" 0
                    guifield cg_password 28
                ]
            ]
            guibar
            guilist [
                guitext (concatword "Bots: " $cg_bots) 0
                guislider cg_bots 0 12
                guitext (concatword "Bot skill: " $cg_skill) 0
                guislider cg_skill 1 101
                guitext (concatword "Round length: " $cg_duration " seconds") 0
                guislider cg_duration 180 900
                guistrut 1
                guiimage (concatword "packages/base/" $cg_map ".jpg") [] 4 1 "data/cube.png" $cg_map
            ]
        ]
        guibar
        guilist [
            guibutton "Create game" [cgcreate] "cube"
            guibar
            guibutton "Cancel" [cleargui 1] "exit"
        ]
    ] "custom game"

    newgui customgamemode [
        guilist [
            guilist [
                guibutton "Free For All" [cg_mode = "ffa"; cg_mode_label = "Free For All"; showgui customgame]
                guibutton "Teamplay" [cg_mode = "teamplay"; cg_mode_label = "Teamplay"; showgui customgame]
                guibutton "Instagib" [cg_mode = "insta"; cg_mode_label = "Instagib"; showgui customgame]
                guibutton "Instagib Team" [cg_mode = "instateam"; cg_mode_label = "Instagib Team"; showgui customgame]
                guibutton "Efficiency" [cg_mode = "effic"; cg_mode_label = "Efficiency"; showgui customgame]
                guibutton "Efficiency Team" [cg_mode = "efficteam"; cg_mode_label = "Efficiency Team"; showgui customgame]
                guibutton "Tactics" [cg_mode = "tac"; cg_mode_label = "Tactics"; showgui customgame]
                guibutton "Tactics Team" [cg_mode = "tacteam"; cg_mode_label = "Tactics Team"; showgui customgame]
            ]
            guibar
            guilist [
                guibutton "Capture" [cg_mode = "capture"; cg_mode_label = "Capture"; showgui customgame]
                guibutton "Regen Capture" [cg_mode = "regencapture"; cg_mode_label = "Regen Capture"; showgui customgame]
                guibutton "CTF" [cg_mode = "ctf"; cg_mode_label = "Capture The Flag"; showgui customgame]
                guibutton "Instagib CTF" [cg_mode = "instactf"; cg_mode_label = "Instagib CTF"; showgui customgame]
                guibutton "Protect" [cg_mode = "protect"; cg_mode_label = "Protect"; showgui customgame]
                guibutton "Hold" [cg_mode = "hold"; cg_mode_label = "Hold"; showgui customgame]
                guibutton "Collect" [cg_mode = "collect"; cg_mode_label = "Collect"; showgui customgame]
            ]
        ]
    ] "game mode"

    newgui customgamemaps [
        guilist [
            guistrut 17 1
            guilist [guistrut 15 1; gencgmapitems $ffamaps1]
            guilist [guistrut 15 1; gencgmapitems $ffamaps2]
            guilist [guistrut 15 1; gencgmapitems $ffamaps3]
        ]
        guitab "ffa 2"
        guilist [
            guistrut 17 1
            guilist [guistrut 15 1; gencgmapitems $ffamaps4]
            guilist [guistrut 15 1; gencgmapitems $ffamaps5]
            guilist [guistrut 15 1; gencgmapitems $ffamaps6]
        ]
        guitab "ffa 3"
        guilist [
            guistrut 17 1
            guilist [guistrut 15 1; gencgmapitems $ffamaps7]
            guilist [guistrut 15 1; gencgmapitems $ffamaps8]
            guilist [guistrut 15 1; gencgmapitems $ffamaps9]
        ]
        guitab "ffa 4"
        guilist [
            guistrut 17 1
            guilist [guistrut 15 1; gencgmapitems $ffamaps10]
            guilist [guistrut 15 1; gencgmapitems $ffamaps11]
            guilist [guistrut 15 1]
        ]
        guitab "capture 1"
        guilist [
            guistrut 17 1
            guilist [guistrut 15 1; gencgmapitems $capturemaps1]
            guilist [guistrut 15 1; gencgmapitems $capturemaps2]
            guilist [guistrut 15 1; gencgmapitems $capturemaps3]
        ]
        guitab "capture 2"
        guilist [
            guistrut 17 1
            guilist [guistrut 15 1; gencgmapitems $capturemaps4]
            guilist [guistrut 15 1; gencgmapitems $capturemaps5]
            guilist [guistrut 15 1; gencgmapitems $capturemaps6]
        ]
        guitab "capture 3"
        guilist [
            guistrut 17 1
            guilist [guistrut 15 1; gencgmapitems $capturemaps7]
            guilist [guistrut 15 1]
            guilist [guistrut 15 1]
        ]
        guitab "ctf 1"
        guilist [
            guistrut 17 1
            guilist [guistrut 15 1; gencgmapitems $ctfmaps1]
            guilist [guistrut 15 1; gencgmapitems $ctfmaps2]
            guilist [guistrut 15 1; gencgmapitems $ctfmaps3]
        ]
        guitab "ctf 2"
        guilist [
            guistrut 17 1
            guilist [guistrut 15 1; gencgmapitems $ctfmaps4]
            guilist [guistrut 15 1; gencgmapitems $ctfmaps5]
            guilist [guistrut 15 1; gencgmapitems $ctfmaps6]
        ]
        guitab "ctf 3"
        guilist [
            guistrut 17 1
            guilist [guistrut 15 1; gencgmapitems $ctfmaps7]
            guilist [guistrut 15 1]
            guilist [guistrut 15 1]
        ]
        guitab "other"
        guilist [
            guistrut 17 1
            guilist [guistrut 15 1; gencgmapitems $conceptmaps]
            guilist [guistrut 15 1; gencgmapitems $spmaps]
            guilist [guistrut 15 1; gencgmapitems $rpgmaps]
        ]
        guitab "custom"
        guilist [
            guistrut 17 1
            guilist [guistrut 15 1; gencgmapitems $cgcustommaps]
        ]
    ] "choose map"

    newgui joinpassword [
        guitext (concatword "Password required for " $cg_join_target) 0
        guifield cg_join_password 28
        guibar
        guilist [
            guibutton "Join" [cleargui 1; join $cg_join_target $cg_join_password] "cube"
            guibar
            guibutton "Cancel" [cleargui 1] "exit"
        ]
    ] "password"

    newgui content [
        guibutton "mods.."  "showgui mods"
        guibutton "put mods in url.."  [js "Module.assets.modsToURL()"]
        guibutton "reload page.."  [js "window.location.reload()"]
    ]

    ${internalServers}

    injectedmenu = [
        guilist [
          guiimage (concatword "packages/icons/" (playermodelicon) ".jpg") [chooseplayermodel] 1.15
          guistrut 0.25
          guilist [
              newname = (getname)
              guifield newname 15 [name $newname]
              guispring
              guilist [
                  guibutton (playermodelname) [chooseplayermodel] 0
                  guistrut 1
                  guiimage (getcrosshair) [showgui crosshair] 0.5
              ]
          ]
      ]
      guibar
      if (isconnected) [
          if (|| $editing (m_edit (getmode))) [
              guibutton "editing.." "showgui editing"
          ]
          guibutton "vote game mode / map.." "showgui gamemode"
          guibutton "switch team" [if (strcmp (getteam) "good") [team evil] [team good]]
          guibutton "toggle spectator" [spectator (! (isspectator (getclientnum)))] "spectator"
          guibutton "master.." [showgui master]
          guibutton "disconnect" "disconnect"         "exit"
          guibar
      ]
      guibutton "server browser.." "showgui integrated"
      guibutton "create custom game..." [showgui customgame]
      guibutton "random map.."  "map random"
      guibutton "content.." "showgui content"
      if ($fullscreen) [
          guibutton "exit fullscreen.." [fullscreen 0]
      ] [
          guibutton "enter fullscreen.." [fullscreen 1]
      ]
      guibutton "options.."        "showgui options"
      guibutton "about.."          "showgui about"
    ]
    `
    BananaBread.execute(menu)
  }, [authState, state])

  React.useEffect(() => {
    // All commands in flight
    let commands: CommandRequest[] = []

    const [serverURL] = CONFIG.servers

    const { protocol } = window.location
    const wsProtocol = protocol === 'https:' ? 'wss:' : 'ws:'
    const sameHost = serverURL.startsWith(window.location.host)
    const baseWsPath = new URL('ws/', window.location.href).pathname
    const targetUrl = sameHost
      ? `${wsProtocol}//${window.location.host}${baseWsPath}`
      : `${wsProtocol}//${serverURL}`
    const ws = new WebSocket(targetUrl)
    ws.binaryType = 'arraybuffer'

    ws.onopen = () => {
      const { current: queue } = wsQueue
      if (queue == null) return
      for (const message of queue) {
        send(message)
      }
    }

    wsRef.current = ws

    const runCommand = async (command: string) => {
      const generate = (): number => Math.floor(Math.random() * 2048)

      let id: number = generate()

      // We don't want collisions and can't use a Symbol
      while (R.find((v) => v.id === id, commands) != null) {
        id = generate()
      }

      const promiseSet = breakPromise<string>()

      commands = [
        ...commands,
        {
          id,
          promiseSet,
        },
      ]

      const message: CommandMessage = {
        Op: MessageType.Command,
        Command: command,
        Id: id,
      }

      send(CBOR.encode(message))

      return promiseSet.promise
    }

    const injectServers = (servers: any) => {
      const count = servers?.length ?? 0
      if (!servers || count === 0) {
        console.log('[sour] no servers to inject; keeping existing list')
        return
      }
      // Clear previous list entirely to avoid conflicting state
      BananaBread.execute('clearservers 1')
      console.log('[sour] injecting servers:', count)
      R.map((server) => {
        const { Host, Port, Info, Length } = server

        // Get data byte size, allocate memory on Emscripten heap, and get pointer
        const pointer = Module._malloc(Length)

        // Copy data to Emscripten heap (directly accessed from Module.HEAPU8)
        const dataHeap = new Uint8Array(Module.HEAPU8.buffer, pointer, Length)
        const source =
          Info instanceof Uint8Array
            ? new Uint8Array(Info.buffer, Info.byteOffset, Length)
            : new Uint8Array(Info, 0, Length)
        dataHeap.set(source)

        // Call function and get result
        BananaBread.injectServer(Host, Port, pointer, Length)

        // Mark injected entries as kept so engine maintenance doesn't remove them
        BananaBread.execute(`keepserver ${Host} ${Port}`)

        // Free memory
        Module._free(pointer)
      }, servers)
      BananaBread.execute('sortservers')
    }

    let serverEvents: SocketMessage[] = []
    let queuedEvents: SocketMessage[] = []
    let loadingWorld = false

    Module.running = false
    Module.postLoadWorld = function () {
      loadingWorld = false
      serverEvents = [...serverEvents, ...queuedEvents]
      queuedEvents = []
    }
    Module.addRunDependency = (file) => {
      console.log(`add ${file}`)
    }

    Module.interop = (command: string): number => {
      try {
        const result = window.eval(command)
        if (result == null) {
          return 0
        }
        const numBytes = lengthBytesUTF8(result) + 1
        const onHeap = Module._malloc(numBytes)
        stringToUTF8(result, onHeap, numBytes)
        // Sauer frees this on its own when it's done
        return onHeap
      } catch (e) {
        console.error(`failed to eval '${command}'`, e)
        return 0
      }
    }

    let remoteConnected: boolean = false

    let cachedServers: Maybe<any> = null
    Module.onGameReady = () => {
      onReadyAssets()
      Module.FS_createPath(`/`, 'packages', true, true)
      Module.FS_createPath(`/packages`, 'base', true, true)
      Module.FS_createPath(`/packages`, 'prefab', true, true)
      Module.FS_createPath(`/`, 'demo', true, true)

      if (BROWSER.isFirefox || BROWSER.isSafari) {
        // Disable effects for Firefox/Safari due to WebGL performance issues
        BananaBread.execute('skipparticles 1')
        BananaBread.execute('glare 0')
      } else {
        // Full quality for other browsers
        BananaBread.execute('skipparticles 0')
        BananaBread.execute('glare 1')
      }

      if (!BROWSER.isMobile) {
        BananaBread.execute(`
              fullscreendesktop 1
        `)
      }

      if (BROWSER.isMobile) {
        BananaBread.execute(`
              // mobile screens are really dark
              lazyshader 0 "mobilegamma" (fsvs) (fsps [gl_FragColor.rgb = pow(color.rgb, vec3(1.0/1.8));])
              setpostfx mobilegamma
              forceplayermodels
              playermodel 1
              gui2d 1
              skyboxglare 0
              fullscreendesktop 0
              skipskybox 1
        `)
      }

      Module.running = true
      setResolution(null, null)
      setState({
        type: GameStateType.Ready,
      })

      // Re-enable automatic updates (built-in tab is rendered separately)
      BananaBread.execute('autoupdateservers 1')

      if (cachedServers != null) {
        injectServers(cachedServers)
      }

      if (loadedMods.length > 0) {
        log.success(
          `loaded mods: ${R.join(
            ', ',
            loadedMods
          )}. if you experience issues, add ?safemode to the URL.`
        )
      }
      if (failedMods.length > 0) {
        log.error(`failed to load mods: ${R.join(', ', failedMods)}`)
      }

      const {
        location: { search: params, hash },
      } = window

      const serverDestination = SERVER_URL_REGEX.exec(hash)
      const mapDestination = MAP_URL_REGEX.exec(hash)
      if (serverDestination != null) {
        const [, hostname, port] = serverDestination
        if (port == null) {
          BananaBread.execute(`join ${hostname}`)
        } else {
          BananaBread.execute(`connect ${hostname} ${port}`)
        }
      } else if (mapDestination != null) {
        const [, mapId] = mapDestination
        BananaBread.execute(`map ${mapId}`)
      } else if (hash.startsWith('/demo/')) {
        // First check the fragment for a URL
        if (hash.length !== 0) {
          const url = hash.slice(1)
          remoteConnected = true
          playDemoURL(url, 'from-url')
        } else {
          const demoDestination = DEMO_URL_REGEX.exec(hash)
          if (demoDestination != null) {
            const [, demoId] = demoDestination
            const [serverURL] = CONFIG.servers
            const { protocol } = window.location
            const demoURL = `${protocol}//${serverURL}api/demo/${demoId}`
            remoteConnected = true
            playDemoURL(demoURL, demoId)
          } else {
            pushURLState('#')
          }
        }
      } else {
        // It should not be anything else
        pushURLState('#')
      }

      const parsedParams = new URLSearchParams(params)

      initializeDiscord(
        parsedParams.has('code') ? parsedParams.get('code') : null
      )

      if (parsedParams.has('cmd')) {
        const cmd = parsedParams.get('cmd')
        if (cmd == null) return
        setTimeout(() => BananaBread.execute(cmd), 0)
      }

      if (parsedParams.has('safemode')) {
        log.info(
          'you are in safe mode. no mods were loaded, but you can disable them in the mod menu.'
        )
      }
    }

    const updateServerURL = (name: string, port: number) => {
      // Sour server
      if (port === 0) {
        pushURLState(`#/server/${name}`)
        return
      }

      pushURLState(`#/server/${name}/${port}`)
    }

    Module.onConnect = () => {}
    Module.onDisconnect = () => {
      remoteConnected = false
      clearURLState()
    }

    Module.loadedMap = (name: string) => {
      if (remoteConnected) return
      pushURLState(`#/map/${name}`)
    }

    let lastPointer: number = 0
    let lastPointerLength: number = 0

    // Only allocate memory if we really need to
    const malloc = (size: number) => {
      // reduce, reuse, recycle
      if (size <= lastPointerLength) {
        return lastPointer
      }

      if (lastPointer !== 0) {
        Module._free(lastPointer)
      }

      const pointer = Module._malloc(size)
      lastPointer = pointer
      lastPointerLength = size
      return pointer
    }

    Module.onLocalDisconnect = () => {
      clearURLState()
    }

    Module.cluster = {
      createGame: (preset: string, mode: string) => {
        ;(async () => {
          try {
            let gameMode = mode || preset || 'ffa'
            let map = 'complex'
            let visibility = 'unlisted'
            let password = ''
            let bots = 0
            let skill = 70
            let duration = 300

            // Native CubeScript GUI sends one compact spec through the existing
            // engine creategame command. Parse values from the end so passwords
            // may contain the delimiter without corrupting the remaining fields.
            if (preset.includes('|')) {
              const parts = preset.split('|')
              gameMode = parts.shift() || 'ffa'
              map = parts.shift() || 'complex'
              const visibilityCode = parts.shift() || '2'
              visibility = visibilityCode === '0' ? 'public' : visibilityCode === '1' ? 'password' : 'unlisted'
              duration = Number(parts.pop() || 300)
              skill = Number(parts.pop() || 70)
              bots = Number(parts.pop() || 0)
              password = parts.join('|')
            }

            if (visibility === 'password' && password.trim().length < 3) {
              throw new Error('Password must be at least 3 characters.')
            }

            const title = `${gameMode} on ${map}`
            const args = [
              'creategame',
              gameMode,
              map,
              `visibility=${visibility}`,
              `password=${encodeCommandValue(password)}`,
              `bots=${Math.max(0, Math.min(12, Math.trunc(bots)))}`,
              `skill=${Math.max(1, Math.min(101, Math.trunc(skill)))}`,
              `duration=${Math.max(180, Math.min(900, Math.trunc(duration)))}`,
              `title=${encodeCommandValue(title)}`,
            ]
            await runCommand(args.join(' '))
            log.success('custom game created; share the URL or room code with friends')
          } catch (e) {
            log.error(`failed to create game: ${e}`)
          }
        })()
      },
      connect: (name: string, password: string) => {
        const target = name.length === 0 ? 'ffa' : name
        ;(async () => {
          try {
            const encoded = password ? ` ${encodeCommandValue(password)}` : ''
            await runCommand(`join ${target}${encoded}`)
          } catch (e) {
            const message = String(e)
            if (!password && message.toLowerCase().includes('incorrect password')) {
              const safeTarget = target.replace(/[^A-Za-z0-9_-]/g, '')
              BananaBread.execute(`cg_join_target = "${safeTarget}"; cg_join_password = ""; showgui joinpassword`)
              return
            }
            log.error(`failed to join ${target}: ${e}`)
          }
        })()
      },
      send: (channel: number, dataPtr: number, dataLength: number) => {
        const packet = new Uint8Array(dataLength)
        packet.set(new Uint8Array(Module.HEAPU8.buffer, dataPtr, dataLength))
        if (DEBUG) {
          const p = cube.newPacket(packet)
          const msgType = cube.getInt(p)
          if (msgType != null) {
            console.log(
              `%c client -> server type=${CubeMessageType[msgType]}`,
              'background-color: blue; color: white'
            )
          }
        }
        send(
          CBOR.encode({
            Op: MessageType.Packet,
            Channel: channel,
            Data: packet,
            Length: dataLength,
          })
        )
      },
      receive: (dataPtr: number, dataLengthPtr: number) => {
        const view = new DataView(Module.HEAPU8.buffer)

        const message = serverEvents.shift()
        if (message == null) {
          return 0
        }

        if (message.Op === MessageType.ServerConnected) {
          // Layout:
          // 2: Event
          const frameLength = 2
          const pointer = malloc(frameLength)
          view.setUint16(pointer, ENetEventType.Connect, true)
          return pointer
        }

        if (message.Op === MessageType.ServerDisconnected) {
          const { Reason } = message

          // Layout:
          // 2: Event
          // 2: Reason
          const frameLength = 2 + 2
          const pointer = malloc(frameLength)
          view.setUint16(pointer, ENetEventType.Disconnect, true)
          view.setUint16(pointer + 2, Reason, true)
          return pointer
        }

        const { Channel, Data, Length } = message

        // Layout:
        // 2: Event
        // 2: Channel
        // 4: Length
        // N: Data
        const frameLength = 2 + 2 + 4 + Length
        const pointer = malloc(frameLength)

        // sourEvent
        view.setUint16(pointer, ENetEventType.Receive, true)
        // sourChannel
        view.setUint16(pointer + 2, Channel, true)
        // dataLength
        view.setUint32(pointer + 4, Length, true)

        // Copy in from data
        const dataHeap = new Uint8Array(
          Module.HEAPU8.buffer,
          pointer + 8,
          Length
        )
        dataHeap.set(new Uint8Array(Data.buffer, Data.byteOffset, Length))

        return pointer
      },
      disconnect: () => {
        clearURLState()
        send(
          CBOR.encode({
            Op: MessageType.Disconnect,
          })
        )
      },
    }

    ws.onmessage = (evt) => {
      const serverMessage: ServerMessage = CBOR.decode(evt.data)

      if (serverMessage.Op === MessageType.Info) {
        const { Cluster, Master } = serverMessage
        const combined = [...(Master || [])]

        // Rebuild the Servers GUI with a Built in tab based on Cluster
        let gui: Maybe<string> = null
        try {
          const builtins = (Cluster || []) as any[]
          const rows = builtins
            .map(
              (s: any) => {
                const lock = s.Password ? '^f3[locked]^f7 ' : ''
                const custom = s.Temporary ? '^f6[custom]^f7 ' : ''
                const bots = s.Bots > 0 ? `, ${s.Bots} bot${s.Bots === 1 ? '' : 's'}` : ''
                const label = `${lock}${custom}${s.Alias} (^f2${s.NumClients} player${s.NumClients === 1 ? '' : 's'}${bots}^f7) - ${modeName(s.Mode)} ${s.Map}`
                const action = s.Password
                  ? `[cg_join_target = \"${s.Alias}\"; cg_join_password = \"\"; showgui joinpassword]`
                  : `[join ${s.Alias}]`
                return `guibutton "${label}" ${action}`
              }
            )
            .join("\n")

          // Create a new, separate GUI so we don't mutate the stock "servers" GUI
          gui = `newgui integrated [
            ${rows}
            guitab "servers"
            guiservers [
              guilist [
                guicheckbox \"auto-sort\" autosortservers
                if (= $autosortservers 0) [ guibar ; guibutton \"sort\" \"sortservers\" ]
              ]
              guibar
            ] 17
          ] "" [initservers]`
          setInternalServers(gui)
        } catch (e) {
          console.warn('failed to build built-in servers tab', e)
        }

        if (
          BananaBread == null ||
          BananaBread.execute == null ||
          BananaBread.injectServer == null
        ) {
          cachedServers = combined
          return
        }

        // Inject only master into engine list as before
        injectServers(combined)
        return
      }

      if (serverMessage.Op === MessageType.ServerConnected) {
        remoteConnected = true
        const { Server, Internal, Owned } = serverMessage
        if (Internal) {
          clearURLState()
        } else {
          updateServerURL(Server, 0)
          if (Owned) {
            navigator.clipboard.writeText(location.href)
          }
        }
        // intentional fallthrough
      }

      if (serverMessage.Op === MessageType.ServerResponse) {
        const { Id, Response, Success } = serverMessage
        const request = R.find(({ id: otherId }) => Id === otherId, commands)
        if (request == null) return

        const {
          promiseSet: { resolve, reject },
        } = request

        if (Success) {
          resolve(Response)
        } else {
          reject(new Error(Response))
        }

        commands = R.filter(({ id: otherId }) => Id !== otherId, commands)
        return
      }

      if (serverMessage.Op === MessageType.Packet) {
        const packet = cube.newPacket(serverMessage.Data)
        const msgType = cube.getInt(packet)

        if (msgType === CubeMessageType.N_MAPCHANGE) {
          loadingWorld = true
          serverEvents.push(serverMessage)
          return
        }

        if (msgType != null) {
          if (DEBUG) {
            console.log(
              `%c server -> client type=${CubeMessageType[msgType]}`,
              'background-color: green; color: white'
            )
          }
          if (loadingWorld && !DELAY_AFTER_LOAD.includes(msgType)) {
            queuedEvents.push(serverMessage)
            return
          }
        }
      }

      if (serverMessage.Op === MessageType.AuthSucceeded) {
        receiveAuthMessage(serverMessage)
        return
      }

      if (serverMessage.Op === MessageType.Chat) {
        log.chat(serverMessage.Message)
        return
      }

      if (serverMessage.Op === MessageType.AuthFailed) {
        receiveAuthMessage(serverMessage)
        return
      }

      if (loadingWorld) {
        queuedEvents.push(serverMessage)
      } else {
        serverEvents.push(serverMessage)
      }
    }
  }, [])

  React.useLayoutEffect(() => {
    const { canvas } = Module
    if (canvas == null) return

    // As a default initial behavior, pop up an alert when webgl context is lost. To make your
    // application robust, you may want to override this behavior before shipping!
    // See http://www.khronos.org/registry/webgl/specs/latest/1.0/#5.15.2
    canvas.addEventListener(
      'webglcontextlost',
      function (e) {
        alert('WebGL context lost. You will need to reload the page.')
        e.preventDefault()
      },
      false
    )

    //canvas.addEventListener('click', function () {
    //canvas.requestPointerLock()
    //})

    return
  }, [])

  return (
    <OuterContainer ref={containerRef}>
      <GameContainer>
        <canvas
          className="game"
          style={{ opacity: state.type !== GameStateType.Ready ? 0 : 1 }}
          id="canvas"
          tabIndex={0}
          ref={(canvas) => {
            if (canvas != null) {
              // This is a bug in mobile Safari where Reader holds on to canvas refs
              // https://gist.github.com/eugeneware/bb69a5bce8c8c48178845429435458e2#file-safari_reader-js-L1937
              // @ts-ignore
              canvas._evaluatedForTextContent = true
              // @ts-ignore
              canvas._cachedElementBoundingRect = {}
              // Ensure the canvas is focusable for keyboard events
              // (SDL with Emscripten binds keyboard to the target element)
              // eslint-disable-next-line no-param-reassign
              canvas.tabIndex = 0
            }
            Module.canvas = canvas
          }}
          onMouseDown={(_e: React.MouseEvent<HTMLCanvasElement>) => {
            // Focus the canvas so key events are delivered here
            // (important when SDL binds to #canvas)
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            const el = document.getElementById('canvas') as HTMLCanvasElement | null
            if (el) el.focus()
          }}
          onContextMenu={(event) => event.preventDefault()}
        ></canvas>
        {BROWSER.isMobile && (
          <MobileControls
            playerState={playerState}
            isRunning={state.type === GameStateType.Ready}
          />
        )}
      </GameContainer>
      {state.type !== GameStateType.Ready && (
        <LoadingContainer>
          <Box w="100%" h="100%">
            <StatusOverlay state={state} />
          </Box>
        </LoadingContainer>
      )}
      <FileDropper>
        <DropTarget>
          <Flex align="center" justify="center">
            <VStack paddingTop="20%">
              <Heading>Drop a demo file here...</Heading>
            </VStack>
          </Flex>
        </DropTarget>
      </FileDropper>
    </OuterContainer>
  )
}

ReactDOM.render(
  <ChakraProvider theme={theme}>
    <App />
  </ChakraProvider>,
  document.getElementById('root')
)
